// SessionHelpProvider — the room-recovery layer that sits above every screen
// (Part 2, spec §6 / M4).
//
// Mounted in App.jsx ABOVE the route branches, so it wraps GameFlowProvider on
// /game (and so sits outside ScreenErrorBoundary: a crash loop on the GM's
// screen still leaves Support and a GM handover reachable, incident #15) and
// the public branch for /lobby and /waitingroom. It does not depend on
// GameFlowProvider. It reads the roster, the live row and the viewer's own GM
// status itself, and GameFlowProvider registers into it (registerGameFlow)
// when it is there.
//
// What it owns:
//   - one realtime channel (unique topic) over the seat, vote, ballot, readmit
//     and live-state tables, all filtered by session;
//   - the presence heartbeat (touch_seat every 15 s while visible);
//   - the viewer's own seat state (the "You're marked away" banner);
//   - the open vote: banner, sheet, and resolving it when its time comes;
//   - the Help & session sheet and its GM-only recovery actions;
//   - readmit requests for every active seat (migration 275: anyone at the
//     table can let a player back in), and the "<name> let <name> back in"
//     line on every phone when one is approved.
//
// Everything is dark until the session's features say otherwise: with every
// flag off (production today) the only traffic is game_session_features,
// once a minute.
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { toast } from 'sonner';
import { supabase } from '@tasted/utils/supabase';
import { randomUUID } from '@tasted/utils/randomId';
import { claimTokenStorage, gmSecretStorage, playerStorage } from '../../utils/playerStorage';
import useSessionFeatures from '../../hooks/useSessionFeatures';
import { useCatchUpOnRecovery } from '../../hooks/useCatchUpOnRecovery';
import { resilientChannel } from '../../utils/realtime';
import { readmitRequestStore } from '../../utils/readmit';
import useWakeLock from '../../hooks/useWakeLock';
import { isLateJoinRequest, readmitRequestName } from '../../utils/readmit';
import { createCoalescedRefresh } from '../../utils/coalescedRefresh';
import * as room from '../../utils/roomRecovery';
import {
  clockOffsetFrom,
  isVoteDue,
  isVoteOpen,
  resolveDelays,
  resolvedLine,
} from '../../utils/voteRules';
import {
  HELP_ACTIONS,
  setHelpAnalyticsContext,
  trackHelpOpened,
  trackHelpViewOpened,
  withHelpTracking,
} from '../../utils/helpAnalytics';
import { SessionHelpContext } from './context';
import { GameFlowRegistryContext, createGameFlowRegistry } from '../../contexts/gameFlowRegistry';

// Lane C's useActiveRoster / useRoomFeatures import the context from here.
// Value includes { sessionCode, roster, activeIds, features, … } (spec §6).
export { SessionHelpContext, RegisterGameFlowContext, useSessionHelp, useRegisterGameFlow } from './context';
import HelpErrorBoundary from './HelpErrorBoundary';
import SessionHelpOverlay from './SessionHelpOverlay';

export const HEARTBEAT_MS = 15_000;
const WAKE_THROTTLE_MS = 3_000;
const RESOLVE_COOLDOWN_MS = 4_000;
const SEAT_STATUSES = new Set(['waiting', 'lobby', 'started']);

const ROSTER_COLUMNS =
  'id, username, avatar, is_gamemaster, is_ready, joined_at, is_active, inactive_reason, inactive_since, return_needs_approval';
const ROSTER_COLUMNS_LEGACY = 'id, username, avatar, is_gamemaster, is_ready, joined_at';
// phase_started_at (214): the gates leave out seats that joined after it (233).
const LIVE_COLUMNS = 'run_id, version, step_index, phase, payload, gm_player_id, updated_at, phase_started_at';

let channelSeq = 0;

/**
 * Which surface a path is. The help layer lives on the lobby, the waiting room
 * and every /game screen; nowhere else (not /join, /onboarding, /avatar, /tv,
 * the dev previews, or the post-game public pages).
 */
export function helpSurface(pathname = '') {
  if (pathname === '/lobby' || pathname === '/waitingroom') return 'public';
  if (pathname === '/game' || pathname.startsWith('/game/')) return 'game';
  return null;
}

const EMPTY_VOTE = { vote: null, lastResolved: null, handoverAvailable: false, gmAbsent: null, offset: 0 };

/**
 * Which live row to hold. A different run_id wins unless it is a run this
 * phone has already moved past (`retiredRuns`); within a run, newer-or-equal
 * version wins. Version alone can't decide across runs: the lobby reset
 * trigger (053) starts a new run at version 0, and test-session resets delete
 * the row. Pure; the caller retires the old run when a new one replaces it.
 */
export function preferLiveRow(prev, next, retiredRuns = null) {
  if (!next) return prev;
  if (retiredRuns?.has(next.run_id)) return prev;
  if (!prev) return next;
  if (next.run_id !== prev.run_id) return next;
  return Number(next.version) >= Number(prev.version) ? next : prev;
}

function notYourPlayer(error) {
  return error?.code === '42501' && /not your player/i.test(String(error?.message || ''));
}

export function SessionHelpProvider({ children }) {
  const location = useLocation();
  const surface = helpSurface(location.pathname);
  const sessionCode = surface ? new URLSearchParams(location.search).get('code') || null : null;
  const playerId = sessionCode ? playerStorage.get(sessionCode) : null;
  const enabled = Boolean(sessionCode && playerId);

  const { features, refresh: refreshFeatures } = useSessionFeatures(sessionCode, { enabled });
  // Anything beyond step authority means this layer has work to do. Step
  // authority alone (the first flag to flip in production) changes nothing here.
  const roomOn =
    enabled &&
    (features.room_help_sheet ||
      features.room_votes ||
      features.room_readmit ||
      features.room_active_seats ||
      features.room_restart_question ||
      features.room_reset_session);

  const [session, setSession] = useState(null);
  const [roster, setRoster] = useState([]);
  const [live, setLive] = useState(null);
  const [voteState, setVoteState] = useState(EMPTY_VOTE);
  const [readmits, setReadmits] = useState([]);
  const [budget, setBudget] = useState(null);
  const [tokenRevoked, setTokenRevoked] = useState(false);
  const [help, setHelp] = useState({ open: false, view: 'home' });
  const [inlineCount, setInlineCount] = useState(0);
  const [gameFlow, setGameFlow] = useState(null);

  // Refs so the async fetchers and realtime handlers always read the latest.
  const codeRef = useRef(sessionCode);
  const playerRef = useRef(playerId);
  const featuresRef = useRef(features);
  const rosterRef = useRef(roster);
  const sessionRef = useRef(session);
  const liveRef = useRef(live);
  const retiredRunsRef = useRef(new Set());
  // Set further down (it reads helpVisible); the realtime handler calls it.
  const announceReadmitRef = useRef(null);
  codeRef.current = sessionCode;
  playerRef.current = playerId;
  featuresRef.current = features;
  rosterRef.current = roster;
  sessionRef.current = session;
  liveRef.current = live;

  const me = useMemo(() => roster.find((r) => r.id === playerId) || null, [roster, playerId]);
  const isGm = me?.is_gamemaster === true; // never from live.gm_player_id (N8)
  const gm = useMemo(() => roster.find((r) => r.is_gamemaster) || null, [roster]);
  const activeIds = useMemo(() => {
    const seats = features.room_active_seats ? roster.filter((r) => r.is_active !== false) : roster;
    return new Set(seats.map((r) => r.id));
  }, [roster, features.room_active_seats]);

  // A new session, or a different player on this phone: start clean.
  useEffect(() => {
    setSession(null);
    setRoster([]);
    setLive(null);
    setVoteState(EMPTY_VOTE);
    setReadmits([]);
    setBudget(null);
    setTokenRevoked(false);
    setHelp({ open: false, view: 'home' });
    liveRef.current = null;
    retiredRunsRef.current = new Set();
  }, [sessionCode, playerId]);

  const claimToken = useCallback(() => claimTokenStorage.get(playerRef.current), []);

  const actor = useCallback(
    () => ({ sessionId: codeRef.current, playerId: playerRef.current, claimToken: claimToken() }),
    [claimToken]
  );

  // ── Reads ────────────────────────────────────────────────────────────────
  const fetchSession = useCallback(async () => {
    const code = codeRef.current;
    if (!code) return null;
    const { data, error } = await supabase
      .from('sessions_v2')
      .select('status, flow_steps, started_at, type')
      .eq('id', code)
      .maybeSingle();
    if (codeRef.current !== code || error) return null;
    setSession(data || null);
    return data || null;
  }, []);

  const fetchRoster = useCallback(async () => {
    const code = codeRef.current;
    if (!code) return null;
    let { data, error } = await supabase.from('session_users_v2').select(ROSTER_COLUMNS).eq('session_id', code);
    if (error?.code === '42703') {
      // Seat-lifecycle columns not there yet (212 not applied): every seat is active.
      ({ data, error } = await supabase.from('session_users_v2').select(ROSTER_COLUMNS_LEGACY).eq('session_id', code));
    }
    if (codeRef.current !== code || error || !Array.isArray(data)) return null;
    setRoster(data);
    rosterRef.current = data;
    return data;
  }, []);

  const applyLiveRow = useCallback((row) => {
    if (!row) return;
    const prev = liveRef.current;
    const next = preferLiveRow(prev, row, retiredRunsRef.current);
    if (next !== prev) {
      if (prev && next.run_id !== prev.run_id) retiredRunsRef.current.add(prev.run_id);
      liveRef.current = next;
      setLive(next);
    }
  }, []);

  const fetchLive = useCallback(async () => {
    const code = codeRef.current;
    if (!code) return null;
    const { data, error } = await supabase.from('session_live_state').select(LIVE_COLUMNS).eq('session_id', code).maybeSingle();
    if (codeRef.current !== code || error || !data) return null;
    applyLiveRow(data);
    return data;
  }, [applyLiveRow]);

  const applyVoteResponse = useCallback((data, { fromState = false } = {}) => {
    if (!data) return;
    const offset = clockOffsetFrom(data.server_now);
    setVoteState((prev) => {
      const vote = data.vote || null;
      if (fromState) {
        return {
          vote: isVoteOpen(vote) ? vote : null,
          lastResolved: data.last_resolved || (vote && !isVoteOpen(vote) ? vote : prev.lastResolved),
          handoverAvailable: data.handover_available === true,
          // null until migration 223 is applied (the server didn't say).
          gmAbsent: typeof data.gm_absent === 'boolean' ? data.gm_absent : null,
          offset,
        };
      }
      if (!vote) return { ...prev, offset };
      if (isVoteOpen(vote)) return { ...prev, vote, offset };
      return {
        ...prev,
        vote: prev.vote && prev.vote.id !== vote.id ? prev.vote : null,
        lastResolved: vote,
        offset,
      };
    });
  }, []);

  const fetchVote = useCallback(async () => {
    const code = codeRef.current;
    const pid = playerRef.current;
    if (!code || !pid || !featuresRef.current.room_votes) {
      setVoteState(EMPTY_VOTE);
      return;
    }
    const token = claimTokenStorage.get(pid);
    if (!token) return;
    const res = await room.getSessionVoteState({ sessionId: code, playerId: pid, claimToken: token });
    if (codeRef.current !== code) return;
    if (res.error) {
      if (notYourPlayer(res.error)) setTokenRevoked(true);
      return;
    }
    applyVoteResponse(res.data, { fromState: true });
  }, [applyVoteResponse]);

  const iAmGmNow = () => {
    const pid = playerRef.current;
    return rosterRef.current.some((r) => r.id === pid && r.is_gamemaster);
  };
  const iAmActiveNow = () => {
    const pid = playerRef.current;
    return rosterRef.current.some((r) => r.id === pid && r.is_active !== false);
  };

  // Migration 275: every active seat can let a player back in, so every active
  // seat reads the requests. Left out: a new player asking to join (still the
  // GM's call) unless this phone is the GM, and this seat's own request.
  const fetchReadmits = useCallback(async () => {
    const code = codeRef.current;
    if (!code || !featuresRef.current.room_readmit || !iAmActiveNow()) {
      setReadmits([]);
      return;
    }
    const read = (cols) =>
      supabase.from('session_readmit_requests').select(cols).eq('session_id', code).eq('status', 'pending');
    let { data, error } = await read('id, player_id, status, created_at, expires_at, wrong_code_count, kind, requested_name');
    if (error?.code === '42703') {
      // kind / requested_name arrive with migration 233; until then every
      // request is a returning seat.
      ({ data, error } = await read('id, player_id, status, created_at, expires_at, wrong_code_count'));
    }
    if (codeRef.current !== code || error || !Array.isArray(data)) return;
    const gmNow = iAmGmNow();
    const pid = playerRef.current;
    setReadmits(data.filter((r) => (gmNow || !isLateJoinRequest(r)) && r.player_id !== pid));
  }, []);

  const fetchBudget = useCallback(async () => {
    const code = codeRef.current;
    const f = featuresRef.current;
    if (!code || !(f.room_restart_question || f.room_reset_session) || !iAmGmNow()) return;
    const res = await room.getRecoveryBudget(code);
    if (codeRef.current !== code || res.error || !res.data) return;
    setBudget(res.data);
  }, []);

  const refreshAll = useCallback(async () => {
    if (!codeRef.current || !playerRef.current) return;
    await Promise.all([fetchRoster(), fetchLive(), fetchSession()]);
    await Promise.all([fetchVote(), fetchReadmits(), fetchBudget()]);
  }, [fetchRoster, fetchLive, fetchSession, fetchVote, fetchReadmits, fetchBudget]);

  const refreshAllRef = useRef(refreshAll);
  refreshAllRef.current = refreshAll;

  const refresher = useMemo(
    () => createCoalescedRefresh(() => refreshAllRef.current(), () => {}, { delay: 150 }),
    // One coalescer per session; the ref keeps it calling the latest reader.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionCode, playerId]
  );
  useEffect(() => () => refresher.dispose(), [refresher]);

  // First load, and again whenever a feature flips on or off.
  const featureKey = Object.entries(features).map(([k, v]) => `${k}:${v ? 1 : 0}`).join(',');
  useEffect(() => {
    if (!roomOn) return;
    refresher.start();
  }, [roomOn, featureKey, refresher]);

  // GM status and whether this seat is active decide what else is worth
  // reading (readmits, budget).
  const meActiveNow = me ? me.is_active !== false : false;
  useEffect(() => {
    if (!roomOn) return;
    fetchReadmits();
    fetchBudget();
  }, [roomOn, isGm, meActiveNow, fetchReadmits, fetchBudget]);

  // ── Realtime: one channel, unique topic (TAS-150) ─────────────────────────
  // C4 (edge-case audit 2026-09-28): on resilientChannel. This is the screen a
  // stuck player is sent to, and one CHANNEL_ERROR used to kill votes, readmit
  // requests and the roster for good. Now a failure rebuilds the channel
  // (fresh topic per attempt), and every SUBSCRIBED — first or after a
  // reconnect — re-reads everything, so nothing missed while down stays missed.
  useEffect(() => {
    if (!roomOn || !sessionCode) return undefined;
    const filter = `session_id=eq.${sessionCode}`;
    const refresh = () => refresher.refresh();
    let dispose = null;
    try {
      dispose = resilientChannel({
        topic: () => `session-help:${sessionCode}:${++channelSeq}`,
        meta: { sessionCode, context: 'session-help' },
        setup: (channel) => channel
          .on('postgres_changes', { event: '*', schema: 'public', table: 'session_users_v2', filter }, refresh)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'session_votes', filter }, refresh)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'session_vote_ballots', filter }, refresh)
          .on('postgres_changes', { event: '*', schema: 'public', table: 'session_readmit_requests', filter }, (payload) => {
            announceReadmitRef.current?.(payload);
            refresh();
          })
          .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'session_live_state', filter }, (payload) => {
            const row = payload?.new;
            const prev = liveRef.current;
            applyLiveRow(row);
            // A GM change bumps the version without moving the step; the roster
            // and the vote are what changed, so read them again.
            if (row && prev && row.gm_player_id !== prev.gm_player_id) refresh();
            if (sessionRef.current?.status !== 'started') fetchSession();
          }),
        onStatus: (status) => {
          if (status === 'SUBSCRIBED') refresh();
        },
      });
    } catch (err) {
      console.warn('[session-help] realtime subscribe failed', err);
    }
    return () => {
      try {
        dispose?.();
      } catch {
        /* already gone */
      }
    };
  }, [roomOn, sessionCode, refresher, applyLiveRow, fetchSession]);

  // Focus / online / visibility catch-up for everything (jittered, throttled).
  useCatchUpOnRecovery(Boolean(roomOn), () => refresher.refresh());

  // ── Presence heartbeat (H3) ───────────────────────────────────────────────
  const seated = Boolean(me) && !tokenRevoked && SEAT_STATUSES.has(session?.status);
  const heartbeatOn = Boolean(roomOn) && seated;
  useEffect(() => {
    if (!heartbeatOn) return undefined;
    let stopped = false;
    let lastAt = 0;
    const beat = async (force = false) => {
      if (stopped) return;
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (!force && now - lastAt < WAKE_THROTTLE_MS) return;
      lastAt = now;
      const token = claimTokenStorage.get(playerRef.current);
      if (!token) return;
      const { error } = await room.touchSeat({ sessionId: codeRef.current, playerId: playerRef.current, claimToken: token });
      if (!error || stopped) return;
      if (notYourPlayer(error)) setTokenRevoked(true);
      if (room.isMissingFunction(error)) stopped = true;
    };
    beat(true);
    const id = setInterval(() => beat(true), HEARTBEAT_MS);
    const onWake = () => beat(false);
    const onVisible = () => {
      if (document.visibilityState === 'visible') beat(false);
    };
    window.addEventListener('focus', onWake);
    window.addEventListener('online', onWake);
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      stopped = true;
      clearInterval(id);
      window.removeEventListener('focus', onWake);
      window.removeEventListener('online', onWake);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [heartbeatOn]);

  // A phone that wakes reads the vote FIRST, before the jittered catch-up, so
  // an open vote shows the moment the screen comes back (N1).
  useEffect(() => {
    if (!roomOn || !features.room_votes) return undefined;
    const onVisible = () => {
      if (document.visibilityState === 'visible') fetchVote();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [roomOn, features.room_votes, fetchVote]);

  // ── Votes: resolve when their time comes (NB1, B1, D1) ─────────────────────
  const lastResolveRef = useRef({ id: null, at: 0 });
  const resolveVote = useCallback(
    async (voteId) => {
      if (!voteId || !featuresRef.current.room_votes) return null;
      const last = lastResolveRef.current;
      if (last.id === voteId && Date.now() - last.at < RESOLVE_COOLDOWN_MS) return null;
      lastResolveRef.current = { id: voteId, at: Date.now() };
      const res = await room.resolveSessionVote({ ...actor(), voteId });
      if (res.error) {
        if (room.isFeatureOff(res.error)) refreshFeatures();
        if (res.error.code === 'P0410') fetchVote();
        return res;
      }
      applyVoteResponse(res.data);
      if (res.data?.vote && !isVoteOpen(res.data.vote)) refresher.refresh();
      return res;
    },
    [actor, applyVoteResponse, fetchVote, refreshFeatures, refresher]
  );

  const openVote = voteState.vote;
  const seatActive = Boolean(me) && me.is_active !== false;
  useEffect(() => {
    if (!features.room_votes || !isVoteOpen(openVote) || !me) return undefined;
    const offset = voteState.offset;
    if (isVoteDue(openVote, offset)) resolveVote(openVote.id);
    // A little past each moment, so this phone's clock is never early.
    const timers = resolveDelays(openVote, offset).map((ms) =>
      setTimeout(() => resolveVote(openVote.id), ms + 400 + Math.floor(Math.random() * 800))
    );
    return () => timers.forEach(clearTimeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    features.room_votes,
    openVote?.id,
    openVote?.status,
    openVote?.effective_status,
    openVote?.min_pass_at,
    openVote?.expires_at,
    voteState.offset,
    Boolean(me),
    resolveVote,
  ]);

  // Lone-survivor votes resolve only if this phone is still awake at the end
  // of the window, so ask the screen to stay on (tolerating a refusal).
  const loneVote = isVoteOpen(openVote) && openVote.mode === 'lone' && seatActive;
  useWakeLock(loneVote);

  // Tell the table how a vote they saw ended.
  const seenVoteRef = useRef(null);
  useEffect(() => {
    if (isVoteOpen(openVote)) {
      seenVoteRef.current = openVote.id;
      return;
    }
    const done = voteState.lastResolved;
    if (done && seenVoteRef.current === done.id) {
      seenVoteRef.current = null;
      const names = Object.fromEntries(rosterRef.current.map((r) => [r.id, r.username]));
      const line = resolvedLine(done, { names });
      if (line) toast(line, { id: `vote-${done.id}` });
    }
  }, [openVote, voteState.lastResolved]);

  const helpVisible =
    Boolean(surface) &&
    enabled &&
    // Review finding 5: room_active_seats marks seats away, and the GM's way
    // to bring one back ("Remove a player" → back) lives in this sheet. So
    // the sheet shows whenever either flag is on; the two can't be flipped
    // apart into a state where an away seat has no way back.
    (features.room_help_sheet || features.room_active_seats) &&
    // Not gated on finding this phone's seat in the roster (Mel, 2026-09-27:
    // a player's question screen had no "?"). A phone that just rejoined or
    // was let back in can be briefly missing from the fetched roster, and
    // support must never disappear then; with no seat it gets the player view.
    // Nor on hydration: a blank loading screen is exactly when a player needs
    // help (Mel, 2026-09-27). The sheet hides Game recovery until the step is
    // known (`hydrating` below), so nothing acts on a guessed step.
    true;
  const hydrating = surface === 'game' && Boolean(gameFlow?.isHydrating);

  // "Someone wants back in — Review" on any screen, once per request, for
  // every active seat (275). `readmits` already leaves out what this phone
  // can't act on.
  const seenReadmitsRef = useRef(new Set());
  useEffect(() => {
    // Review opens the help sheet, so only toast where the sheet can open.
    if (!meActiveNow || !helpVisible) return;
    const now = Date.now();
    for (const r of readmits) {
      if (seenReadmitsRef.current.has(r.id)) continue;
      if (Date.parse(r.expires_at) <= now) continue;
      seenReadmitsRef.current.add(r.id);
      // Say where to act, not just that something happened (Mel, 2026-09-27):
      // the request lives in the Help menu, and the code is on their phone.
      const who = readmitRequestName(r, roster);
      const late = isLateJoinRequest(r);
      // A new player (audit E1) reads differently from someone coming back.
      toast(late ? `New player: ${who || 'someone'} wants to join` : `${who || 'A player'} wants back in`, {
        id: `readmit-${r.id}`,
        description: late
          ? 'Open the ? Help menu → Let a player back in, and type the code on their phone. They score from when they join.'
          : 'Open the ? Help menu → Let a player back in, and type the code on their phone.',
        duration: 15000,
        action: {
          label: 'Open',
          onClick: () => {
            trackHelpOpened({ view: 'let-back-in', entry: 'readmit_toast' });
            setHelp({ open: true, view: 'let-back-in' });
          },
        },
      });
    }
  }, [readmits, meActiveNow, helpVisible, roster]);

  // "<name> let <name> back in", on every phone at the table, when a request
  // is approved (275: whoever approved it, GM or not). From the request row's
  // realtime UPDATE: resolved_by is who let them in. Not on the phone that
  // did it (its row already says so) or the one that was let in.
  announceReadmitRef.current = (payload) => {
    const row = payload?.new;
    if (!row || row.status !== 'approved' || !row.resolved_by) return;
    if (payload.old?.status && payload.old.status !== 'pending') return;
    if (!featuresRef.current.room_readmit || !helpVisible) return;
    const pid = playerRef.current;
    if (row.resolved_by === pid || row.player_id === pid) return;
    const names = rosterRef.current;
    const actorName = names.find((r) => r.id === row.resolved_by)?.username || 'Someone';
    const who = readmitRequestName(row, names) || (isLateJoinRequest(row) ? 'a new player' : 'a player');
    toast(isLateJoinRequest(row) ? `${actorName} let ${who} join` : `${actorName} let ${who} back in`, {
      id: `readmit-done-${row.id}`,
      duration: 4000,
    });
  };

  // ── GameFlowProvider registration (N7, NB5) ────────────────────────────────
  // Lane F's token-scoped registry; GameFlowProvider registers
  // { applyAuthoritative, isHydrating, stepIndex } into it.
  const registry = useMemo(() => createGameFlowRegistry(), []);
  useEffect(() => {
    const sync = (api) =>
      setGameFlow(api ? { isHydrating: Boolean(api.isHydrating), stepIndex: api.stepIndex ?? null } : null);
    const off = registry.subscribe(sync);
    // A child's effect runs before this one, so it may have registered already.
    sync(registry.get());
    return off;
  }, [registry]);

  /**
   * Hand a row the server returned for THIS phone's own call to
   * GameFlowProvider: applyAuthoritative(row, { newRun }) (Lane F). A new run
   * (outcome new_run, or a reset) is applied the way a reset is.
   */
  const applyAuthoritative = useCallback(
    (row, outcome, fromLive = null) => {
      applyLiveRow(row);
      const fn = registry.get()?.applyAuthoritative;
      if (typeof fn !== 'function' || !row) return;
      // A mismatch can carry the NEW run's row (the run moved under us); that
      // has to be applied as a new run too, or the N2 guard drops it.
      const newRun =
        outcome === 'new_run' || outcome === 'reset' || Boolean(fromLive && row.run_id !== fromLive.run_id);
      try {
        Promise.resolve(fn(row, { newRun })).catch((err) =>
          console.warn('[session-help] applyAuthoritative failed; realtime will catch up', err)
        );
      } catch (err) {
        console.warn('[session-help] applyAuthoritative failed; realtime will catch up', err);
      }
    },
    [applyLiveRow, registry]
  );

  // ── Writes ────────────────────────────────────────────────────────────────
  const ensureGmSecret = useCallback(async (force = false) => {
    const code = codeRef.current;
    const pid = playerRef.current;
    if (!code || !pid) return null;
    const cached = gmSecretStorage.get(code);
    if (cached && !force) return cached;
    try {
      const { data, error } = await supabase.rpc('claim_gm_secret_secure', {
        p_session_id: code,
        p_player_id: pid,
        p_claim_token: claimTokenStorage.get(pid),
      });
      if (!error && data) {
        gmSecretStorage.set(code, data);
        return data;
      }
    } catch {
      /* the call below reports the refusal */
    }
    return cached || null;
  }, []);

  const afterCall = useCallback(
    (res) => {
      if (room.isFeatureOff(res.error)) refreshFeatures();
      if (notYourPlayer(res.error)) setTokenRevoked(true);
      return { ...res, ok: !res.error };
    },
    [refreshFeatures]
  );

  /** A GM power: sends the GM secret, and re-claims it once on a 42501. */
  const gmCall = useCallback(
    async (fn, args) => {
      let secret = await ensureGmSecret(false);
      let res = await fn({ ...actor(), ...args, gmSecret: secret });
      if (res.error?.code === '42501' && !notYourPlayer(res.error)) {
        const fresh = await ensureGmSecret(true);
        if (fresh && fresh !== secret) {
          secret = fresh;
          res = await fn({ ...actor(), ...args, gmSecret: secret });
        }
      }
      return afterCall(res);
    },
    [actor, afterCall, ensureGmSecret]
  );

  const actions = useMemo(() => {
    const playerCall = async (fn, args) => afterCall(await fn({ ...actor(), ...args }));

    const raw = {
      /** "Move everyone to the next screen" from the row the confirm showed (M6). */
      async moveEveryoneOn(fromLive) {
        const row = fromLive || liveRef.current;
        if (!row) return { ok: false, copy: room.NETWORK_COPY };
        const res = await gmCall(room.advanceGameStep, {
          runId: row.run_id,
          fromStep: row.step_index,
          expectedVersion: row.version,
          source: 'help_sheet',
        });
        if (res.error) return res;
        const { outcome, live: next } = res.data || {};
        applyAuthoritative(next, outcome, row);
        return { ...res, ok: outcome === 'advanced' || outcome === 'already', outcome, live: next };
      },

      async restartQuestion({ fromLive, roundId }) {
        const row = fromLive || liveRef.current;
        if (!row || !roundId) return { ok: false, copy: "We couldn't find this question. Try again." };
        const res = await gmCall(room.restartQuestion, {
          runId: row.run_id,
          stepIndex: row.step_index,
          roundId,
          expectedVersion: row.version,
        });
        if (res.error) {
          if (res.error.code === 'P0423') fetchBudget();
          return res;
        }
        const { outcome, live: next } = res.data || {};
        applyAuthoritative(next, outcome, row);
        if (res.data?.restart_left != null) setBudget((b) => ({ ...(b || {}), restart_left: res.data.restart_left }));
        return { ...res, ok: outcome === 'restarted', outcome };
      },

      async resetSession({ fromLive }) {
        const row = fromLive || liveRef.current;
        if (!row) return { ok: false, copy: room.NETWORK_COPY };
        const res = await gmCall(room.restartSessionRun, { runId: row.run_id });
        if (res.error) {
          if (res.error.code === 'P0423') fetchBudget();
          return res;
        }
        const { outcome, live: next } = res.data || {};
        applyAuthoritative(next, outcome, row);
        if (res.data?.reset_left != null) setBudget((b) => ({ ...(b || {}), reset_left: res.data.reset_left }));
        return { ...res, ok: outcome === 'reset', outcome };
      },

      async openVote(kind, targetId) {
        const res = await playerCall(room.openSessionVote, { kind, targetId });
        if (res.ok) applyVoteResponse(res.data);
        if (res.error?.code === 'P0409') fetchVote();
        return res;
      },

      async castVote(voteId, choice) {
        const res = await playerCall(room.castSessionVote, { voteId, choice });
        if (res.ok) applyVoteResponse(res.data);
        else fetchVote();
        return res;
      },

      async cancelVote(voteId) {
        const res = await playerCall(room.cancelSessionVote, { voteId });
        if (res.ok) applyVoteResponse(res.data);
        return res;
      },

      async transferGm(newGmId) {
        const res = await gmCall(room.transferGm, { newGmId });
        if (res.ok) applyVoteResponse(res.data);
        if (res.error?.code === 'P0409') fetchVote();
        return res;
      },

      async setSeatActive(targetId, active, { needsApproval = false } = {}) {
        const res = await gmCall(room.setSeatActive, { targetId, active, needsApproval });
        if (res.ok) fetchRoster();
        return res;
      },

      async reactivateMe() {
        const res = await playerCall(room.reactivateMySeat, {});
        if (res.ok) fetchRoster();
        return res;
      },

      /** File a readmit request from THIS phone (the away banner's fallback). */
      async requestReadmitForMe() {
        const mine = rosterRef.current.find((r) => r.id === playerRef.current);
        if (!mine) return { ok: false, copy: room.NETWORK_COPY };
        const requestSecret = randomUUID();
        const res = afterCall(
          await room.requestReadmit({ sessionId: codeRef.current, username: mine.username, requestSecret })
        );
        if (!res.ok) return res;
        // A10: kept with the rest of this phone's identity, so the away
        // banner can pick the request up again after the tab is closed.
        readmitRequestStore.save({
          requestId: res.data.request_id,
          secret: requestSecret,
          sessionCode: codeRef.current,
          username: mine.username,
          playerId: mine.id,
          displayCode: res.data.display_code,
          expiresAt: res.data.expires_at || null,
        });
        return { ...res, requestSecret };
      },

      /** Poll one of this phone's requests; stores the new token on approval. */
      async pollReadmit(requestId, requestSecret) {
        const res = await room.readmitRequestStatus({ requestId, requestSecret });
        if (res.error) return { ...res, ok: false };
        if (res.data?.status === 'approved' && res.data.claim_token) {
          readmitRequestStore.clear(requestId, codeRef.current);
          claimTokenStorage.set(res.data.player_id, res.data.claim_token);
          playerStorage.set(res.data.player_id, codeRef.current);
          setTokenRevoked(false);
          refresher.refresh();
        }
        return { ...res, ok: true };
      },

      async approveReadmit(requestId, code) {
        const res = await gmCall(room.approveReadmit, { requestId, code });
        if (res.ok) fetchReadmits();
        return res;
      },

      async declineReadmit(requestId) {
        const res = await gmCall(room.declineReadmit, { requestId });
        fetchReadmits();
        return res;
      },

      async issueCode(targetId) {
        return gmCall(room.issueReadmitCode, { targetId });
      },

      /** The round and question text behind a question step (for the restart view). */
      async loadQuestion(stepIndex) {
        const steps = sessionRef.current?.flow_steps || [];
        const step = steps[stepIndex];
        if (!step || step.type !== 'question') return null;
        const fromPayload = liveRef.current?.step_index === stepIndex ? liveRef.current?.payload?.round_id : null;
        try {
          const { data } = await supabase.rpc('game_questions', { p_session_id: codeRef.current });
          const match = (Array.isArray(data) ? data : []).find(
            (q) => (fromPayload && q.id === fromPayload) ||
              (q.wine_id === step.wine && Number(q.round_number) === Number(step.index) + 1)
          );
          return { roundId: fromPayload || match?.id || null, text: match?.questions_v2?.text || '' };
        } catch {
          return { roundId: fromPayload || null, text: '' };
        }
      },

      refreshBudget: fetchBudget,
      refresh: () => refresher.refresh(),
    };

    // Every help action reports to Mixpanel as Help Action (started, then
    // succeeded / failed). `action` may depend on the call's arguments.
    const tracked = (name, action, extra = () => ({})) => async (...args) =>
      withHelpTracking(typeof action === 'function' ? action(...args) : action, () => raw[name](...args), {
        extra: extra(...args),
      });
    const A = HELP_ACTIONS;
    return {
      ...raw,
      moveEveryoneOn: tracked('moveEveryoneOn', A.MOVE_EVERYONE_ON),
      restartQuestion: tracked('restartQuestion', A.RESTART_QUESTION),
      resetSession: tracked('resetSession', A.RESET_GAME),
      openVote: tracked('openVote', A.VOTE_OPEN, (kind, targetId) => ({
        vote_kind: kind,
        target_is_self: targetId === playerRef.current,
      })),
      castVote: tracked('castVote', A.VOTE_CAST, (_voteId, choice) => ({ choice: choice ? 'yes' : 'no' })),
      cancelVote: tracked('cancelVote', A.VOTE_CANCEL),
      transferGm: tracked('transferGm', A.HAND_OVER_GM),
      setSeatActive: tracked('setSeatActive', (_targetId, active) => (active ? A.LET_BACK_IN : A.MARK_AWAY), () => ({
        via: 'help_sheet',
      })),
      reactivateMe: tracked('reactivateMe', A.IM_BACK),
      requestReadmitForMe: tracked('requestReadmitForMe', A.READMIT_REQUEST, () => ({ via: 'away_banner' })),
      approveReadmit: tracked('approveReadmit', A.READMIT_APPROVE, () => ({ via: 'help_sheet' })),
      declineReadmit: tracked('declineReadmit', A.READMIT_DECLINE, () => ({ via: 'help_sheet' })),
      issueCode: tracked('issueCode', A.ISSUE_CODE, () => ({ via: 'help_sheet' })),
    };
  }, [actor, afterCall, gmCall, applyAuthoritative, applyVoteResponse, fetchVote, fetchRoster, fetchReadmits, fetchBudget, refresher]);

  const openHelp = useCallback((view = 'home', entry = 'button') => {
    trackHelpOpened({ view, entry });
    setHelp({ open: true, view });
  }, []);
  const closeHelp = useCallback(() => setHelp((h) => ({ ...h, open: false })), []);
  const setHelpView = useCallback((view) => {
    trackHelpViewOpened(view);
    setHelp((h) => ({ ...h, view }));
  }, []);

  // What every help event says about this phone (utils/helpAnalytics).
  const stepIndex = Number.isInteger(live?.step_index) ? live.step_index : gameFlow?.stepIndex ?? null;
  const stepType = Number.isInteger(stepIndex) ? session?.flow_steps?.[stepIndex]?.type ?? null : null;
  useEffect(() => {
    if (!sessionCode) {
      setHelpAnalyticsContext(null);
      return undefined;
    }
    setHelpAnalyticsContext({
      session_id: sessionCode,
      player_id: playerId,
      is_gm: isGm,
      step_index: stepIndex,
      step_type: stepType,
      screen: location.pathname.split('/').filter(Boolean)[0] || null,
      session_type: session?.type ?? null,
    });
    return undefined;
  }, [sessionCode, playerId, isGm, stepIndex, stepType, location.pathname, session?.type]);
  useEffect(() => () => setHelpAnalyticsContext(null), []);
  const registerInline = useCallback(() => {
    setInlineCount((n) => n + 1);
    return () => setInlineCount((n) => Math.max(0, n - 1));
  }, []);

  const flowSteps = session?.flow_steps || [];
  const visible = helpVisible;
  // Readmit works in the lobby too (migration 233, audit A4).
  const seatStatusOk = SEAT_STATUSES.has(session?.status);

  const value = useMemo(
    () => ({
      sessionCode,
      surface,
      playerId,
      roster,
      activeIds,
      me,
      isGm,
      gm,
      live,
      session,
      flowSteps,
      started: session?.status === 'started',
      // The session is in a state a seat can be recovered in (lobby or started).
      seated: seatStatusOk,
      openVote: isVoteOpen(openVote) ? openVote : null,
      lastResolvedVote: voteState.lastResolved,
      clockOffset: voteState.offset,
      handoverAvailable: voteState.handoverAvailable,
      gmAbsent: voteState.gmAbsent,
      hydrating,
      readmits,
      budget,
      features,
      tokenRevoked,
      gameFlow,
      visible,
      showFloating: visible && inlineCount === 0,
      helpOpen: help.open,
      helpView: help.view,
      openHelp,
      closeHelp,
      setHelpView,
      registerInline,
      actions,
    }),
    [sessionCode, surface, playerId, roster, activeIds, me, isGm, gm, live, session, flowSteps, seatStatusOk, openVote, voteState, readmits, budget, features, tokenRevoked, gameFlow, visible, inlineCount, help, openHelp, closeHelp, setHelpView, registerInline, actions]
  );

  return (
    <GameFlowRegistryContext.Provider value={registry.register}>
      <SessionHelpContext.Provider value={value}>
        {children}
        <HelpErrorBoundary resetKey={`${sessionCode}:${location.pathname}`}>
          <SessionHelpOverlay />
        </HelpErrorBoundary>
      </SessionHelpContext.Provider>
    </GameFlowRegistryContext.Provider>
  );
}

export default SessionHelpProvider;
